import { type FormEvent, useState } from 'react'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AuthLayout } from '../components/AuthLayout'
import { GoogleButton } from '../components/GoogleButton'
import { Button, Field, FormError } from '../components/ui'
import { api, call, messageOf } from '../lib/api'
import { safeNext } from '../lib/auth'
import { passkeysSupported, signInWithPasskey, wasCancelled } from '../lib/passkeys'
import { meQuery } from '../lib/queries'

const googleErrors: Record<string, string> = {
  google_cancelled: 'Google sign-in was cancelled.',
  google_expired: 'That took too long. Try Google again.',
  google_unverified: "Google hasn't verified that email address, so we can't use it to sign you in.",
  google_in_use: 'That Google account is already connected to a different account here.',
  account_closed: "An account with that email was closed, so it can't sign up again. Write to hello@fantasycat.co if that's a mistake.",
  google_failed: "Couldn't sign in with Google just now. Try again, or use your password.",
  apple_cancelled: 'Sign in with Apple was cancelled.',
  apple_expired: 'That took too long. Try Apple again.',
  apple_unverified: "Apple didn't share a verified email address, so we can't make your account.",
  apple_in_use: 'That Apple ID is already connected to a different account here.',
  apple_failed: "Couldn't sign in with Apple just now. Try again, or use your password.",
}

export const Route = createFileRoute('/login')({
  validateSearch: (s: Record<string, unknown>): { next?: string; error?: string } => ({ next: safeNext(s.next), error: typeof s.error === 'string' ? s.error : undefined }),
  component: Login,
})

function Login() {
  const { next, error } = Route.useSearch()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [login, setLogin] = useState('')
  const [password, setPassword] = useState('')

  const signIn = useMutation({
    mutationFn: () => call(api.POST('/auth/login', { body: { login, password } })),
    onSuccess: (me) => {
      qc.setQueryData(meQuery.queryKey, me)
      navigate({ to: next ?? '/' })
    },
  })

  const passkey = useMutation({
    mutationFn: signInWithPasskey,
    onSuccess: (me) => {
      qc.setQueryData(meQuery.queryKey, me)
      navigate({ to: next ?? '/' })
    },
  })
  const passkeyError = passkey.isError && !wasCancelled(passkey.error) ? messageOf(passkey.error) : undefined

  // A real <form> posting real name/autocomplete attributes: this is what
  // lets 1Password and iCloud Keychain fill and offer to save.
  const submit = (e: FormEvent) => {
    e.preventDefault()
    signIn.mutate()
  }

  return (
    <AuthLayout
      title="Welcome back"
      footer={
        <>
          New here?{' '}
          <Link to="/signup" search={{ invite: undefined, next }} className="font-semibold text-ink underline underline-offset-2">
            Create an account
          </Link>
        </>
      }
    >
      {passkeysSupported() && (
        <Button block busy={passkey.isPending} onClick={() => passkey.mutate()} id="passkey-signin">
          Sign in with a passkey
        </Button>
      )}
      <GoogleButton next={next} />
      <form onSubmit={submit} className="grid gap-4" method="post" action="/api/auth/login">
        <FormError>{signIn.isError ? messageOf(signIn.error) : (passkeyError ?? googleErrors[error ?? ''])}</FormError>
        <Field id="login-username" label="Username or email" name="username" autoComplete="username" autoCapitalize="none" spellCheck={false} required value={login} onChange={(e) => setLogin(e.target.value)} />
        <Field id="login-password" label="Password" name="password" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
        <Button type="submit" variant="primary" block busy={signIn.isPending}>
          Sign in
        </Button>
        <Link to="/forgot" className="justify-self-center text-[14px] font-semibold text-muted underline underline-offset-2">
          Forgot your password?
        </Link>
      </form>
    </AuthLayout>
  )
}
